import React from "react";
import "./CaseStudy.css";
import { useNavigate } from "react-router-dom";

export default function HelpUsServe() {
  const navigate = useNavigate();

  return (
    <div className="case-page">

      <button className="case-back" onClick={() => navigate(-1)}>
        <svg viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.5 11.5L2.5 7.5M2.5 7.5L6.5 3.5M2.5 7.5H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back to Projects
      </button>

      <div className="case-hero">
        <h1>Help Us Serve — <em>Nonprofit UX</em></h1>
        <p className="case-hero-intro">
          Designed and built a full web presence and first-ever digital donation platform for a 501(c)(3) nonprofit supporting underserved communities — directly enabling thousands of dollars in online fundraising within the first two months.
        </p>
        <div className="case-meta-inline">
          <span><strong>Role</strong>UX Designer &amp; Web Developer</span>
          <span><strong>Duration</strong>Jul 2024 – Nov 2024</span>
          <span><strong>Tools</strong>Figma, WordPress, PayPal</span>
          <span><strong>Type</strong>Nonprofit</span>
        </div>
      </div>

      <div className="case-hero-img has-image">
        <img src="/images/helpusserve-hero.png" alt="Help Us Serve homepage hero showing community volunteers" />
      </div>

      <div className="case-body">

        {/* ── 01 The Problem ── */}
        <div className="case-section">
          <span className="case-section-num">01 — Problem</span>
          <h2>The <em>Problem</em></h2>
          <p>Help Us Serve is a 501(c)(3) nonprofit supporting underserved communities through service and outreach. Despite running real programming on the ground — food drives, community support, and campaigns for families in need — they had no scalable way for supporters to find them online, learn about their work, or contribute. Donations were happening through ad-hoc channels, not a system they could promote, measure, or repeat.</p>
          <p>They needed two things working together: a credible, mission-aligned web presence, and a digital donation system that could actually carry weight.</p>
        </div>

        <div className="case-divider" />

        {/* ── 02 Design Direction ── */}
        <div className="case-section">
          <span className="case-section-num">02 — Direction</span>
          <h2>Design <em>Direction</em></h2>
          <p>For a nonprofit serving vulnerable communities, the design needed to feel warm and human without slipping into either of two common nonprofit pitfalls: looking outdated, or looking so polished it felt corporate. I built a visual identity around three principles:</p>
          <ul>
            <li><strong>Real people, not stock imagery</strong> — The hero leads with an actual photo of community service in action: volunteers packing food boxes. It signals "this is the work" before a single word is read.</li>
            <li><strong>Trust signals up front</strong> — The 501(c)(3) status is called out directly on the hero, and the donation page leads with "100% Secure Donation" so first-time donors feel confident from the start.</li>
            <li><strong>Warm but clear</strong> — A red CTA color that reads as urgent and human, paired with a clean sans-serif and generous spacing so the content stays scannable.</li>
          </ul>
        </div>

        <div className="case-divider" />

        {/* ── 03 Brand & Web Design ── */}
        <div className="case-section">
          <span className="case-section-num">03 — Build</span>
          <h2>Brand &amp; <em>Web Design</em></h2>
          <p>I designed and built the full WordPress platform from scratch:</p>
          <ul>
            <li>Visual identity system aligned with the organization's mission and tone</li>
            <li>Mission, services, and impact pages that tell the story without overwhelming</li>
            <li>Campaign pages designed to be reused — each one pairs a clear narrative with a single donation CTA</li>
            <li>Mobile-responsive layouts so donors can give from any device</li>
            <li>Accessible markup and color contrast so the site reaches as wide an audience as possible</li>
          </ul>
          <div className="case-img">
            <img src="/images/helpusserve-campaign.png" alt="Help Us Serve Food Gift Cards campaign page" />
          </div>
        </div>

        <div className="case-divider" />

        {/* ── 04 Donation Platform ── */}
        <div className="case-section">
          <span className="case-section-num">04 — Build</span>
          <h2>Donation <em>Platform</em></h2>
          <p>I integrated the organization's first-ever digital donation system, turning a manual, hard-to-scale process into a real channel. The flow uses PayPal as the payment processor, supporting both PayPal account and credit/debit card payments.</p>
          <p>Key UX decisions for the donation flow:</p>
          <ul>
            <li><strong>Suggested donation amounts</strong> ($10, $25, $50, $100, $250, $500) with a clear visual default — reduces decision fatigue and anchors donors at higher gift levels than they might enter blind</li>
            <li><strong>Custom amount field</strong> for donors who want to give a specific amount</li>
            <li><strong>Two-step flow</strong> — amount selection first, payment details second — with a donation summary visible before commitment</li>
            <li><strong>Trust signals throughout</strong> — "100% Secure Donation" badge, clear "How it works" explanation, and PayPal branding to leverage existing donor trust</li>
          </ul>

          <div className="case-callout">"Within the first two months, the platform processed hundreds of donations and supported four fundraising campaigns — contributing to over $5,000 raised in digital donations."</div>

          <div className="case-img-row">
            <div className="case-img">
              <img src="/images/helpusserve-donation-form.png" alt="Help Us Serve donation form with suggested amount buttons" />
            </div>
            <div className="case-img">
              <img src="/images/helpusserve-payment.png" alt="Help Us Serve payment details with PayPal and credit card options" />
            </div>
          </div>
          <p className="case-img-caption">Step 1: Amount selection with suggested donation tiers. Step 2: Payment summary with multiple payment options.</p>

          <p>One example campaign — <em>Food Gift Cards</em> — paired a clear emotional story ("Rising food prices and unexpected job losses have left many families struggling to put meals on the table") with a single Donate Now CTA. This template became the model for all four campaigns the platform supported.</p>
        </div>

        <div className="case-divider" />

        {/* ── 05 Project Snapshot ── */}
        <div className="case-section">
          <span className="case-section-num">05 — At a Glance</span>
          <h2>Project <em>Snapshot</em></h2>
          <div className="outcome-grid">
            <div className="outcome-cell">
              <div className="outcome-number">$5K+</div>
              <div className="outcome-desc">Raised through the digital donation platform in the first two months</div>
            </div>
            <div className="outcome-cell">
              <div className="outcome-number">100s</div>
              <div className="outcome-desc">Donations processed online (their first-ever digital donations)</div>
            </div>
            <div className="outcome-cell">
              <div className="outcome-number">4</div>
              <div className="outcome-desc">Fundraising campaigns supported through the platform</div>
            </div>
            <div className="outcome-cell">
              <div className="outcome-number">1st</div>
              <div className="outcome-desc">Digital donation channel established for the organization</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
